import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * CloseButton - for modal/overlay close buttons (dialogs, sheets, drawers)
 * Uses Button component with ghost variant, positioned by consumer
 */
const closeButtonVariants = cva('', {
  variants: {
    size: {
      sm: '',
      md: '',
      lg: '',
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

const buttonSizeMap = {
  sm: 'icon-sm',
  md: 'icon',
  lg: 'icon-lg',
} as const;

export interface CloseButtonProps
  extends Omit<
      React.ComponentProps<typeof Button>,
      'variant' | 'size' | 'children'
    >,
    VariantProps<typeof closeButtonVariants> {
  /** Accessible label for the close button */
  'aria-label'?: string;
}

const CloseButton = React.forwardRef<HTMLButtonElement, CloseButtonProps>(
  (
    { className, size = 'sm', 'aria-label': ariaLabel = 'Close', ...props },
    ref,
  ) => {
    return (
      <Button
        ref={ref}
        data-slot="close-button"
        appearance="ghost"
        variant="secondary"
        size={buttonSizeMap[size ?? 'sm']}
        className={cn(closeButtonVariants({ size }), className)}
        aria-label={ariaLabel}
        {...props}
      >
        <X />
        <span className="sr-only">{ariaLabel}</span>
      </Button>
    );
  },
);

CloseButton.displayName = 'CloseButton';

/**
 * RemoveButton - for inline remove buttons (tags, list items, chips)
 * Lightweight button with smaller footprint
 */
const removeButtonVariants = cva(
  'flex shrink-0 items-center justify-center rounded-[3px] p-0.5 text-muted-foreground hover:bg-surface-1 hover:text-secondary-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  {
    variants: {
      size: {
        sm: '[&>svg]:size-2.5',
        md: '[&>svg]:size-3',
        lg: '[&>svg]:size-3.5',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

export interface RemoveButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof removeButtonVariants> {
  /** Accessible label for the remove button */
  'aria-label'?: string;
}

const RemoveButton = React.forwardRef<HTMLButtonElement, RemoveButtonProps>(
  (
    { className, size = 'md', 'aria-label': ariaLabel = 'Remove', ...props },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        type="button"
        data-slot="remove-button"
        className={cn(removeButtonVariants({ size }), className)}
        aria-label={ariaLabel}
        {...props}
      >
        <X />
      </button>
    );
  },
);

RemoveButton.displayName = 'RemoveButton';

export { CloseButton, closeButtonVariants, RemoveButton, removeButtonVariants };
